import { useState, useEffect } from 'react';
import { useCart } from '../../hooks/useCart';
import { getProductImage } from '../../utils/imageStorage';

export default function ProduitJesusPortraitLivrePlie() {
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const productId = 'jesus-portrait-livre-plie';
  const defaultImage = 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/4dafc94ac260b99dcf978d618eb08d45.png';
  const [productImage, setProductImage] = useState<string>(defaultImage);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' as 'success' | 'error' });

  const product = {
    id: 'jesus-portrait-livre-plie',
    name: 'Jésus – Portrait en livre plié',
    price: 59,
    image: productImage,
    description: 'Création artisanale en livre plié représentant le visage de Jésus. Une pièce unique, réalisée à la main à partir d\'un livre ancien, pour une décoration spirituelle pleine de présence et de poésie.',
    details: {
      fabrication: 'Fait main dans l\'atelier Les Carnets de Lolotte',
      type: 'Pièce unique artisanale',
      dimensions: 'Dimensions : à renseigner (hauteur/largeur/profondeur)',
      delai: '2 à 3 jours',
      materials: 'Livre ancien recyclé (pages imprimées, charme vintage)'
    }
  };

  const handleAddToCart = () => {
    try {
      addToCart({
        id: product.id,
        name: product.name,
        price: product.price,
        image: productImage,
        description: product.description
      }, quantity);
      
      showNotification(`${product.name} ajouté au panier !`, 'success');
    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
      showNotification('Erreur lors de l\'ajout au panier', 'error');
    }
  };

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 3000);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  useEffect(() => {
    const loadImage = async () => {
      const savedImage = await getProductImage(productId);
      if (savedImage) {
        setProductImage(savedImage);
      }
    };
    loadImage();
  }, []);

  useEffect(() => {
    // SEO JSON-LD
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Jésus – Portrait en livre plié (pièce unique)",
      "description": "Portrait de Jésus en livre plié, pièce unique faite main à partir d'un livre ancien. Création poétique et spirituelle – Les Carnets de Lolotte.",
      "image": defaultImage,
      "brand": {
        "@type": "Brand",
        "name": "Les Carnets de Lolotte"
      },
      "offers": {
        "@type": "Offer",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/produit-jesus-portrait-livre-plie`,
        "priceCurrency": "EUR",
        "price": "59",
        "availability": "https://schema.org/LimitedAvailability",
        "itemCondition": "https://schema.org/NewCondition"
      },
      "category": "Art sacré",
      "material": "Papier recyclé, livre ancien"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    // Meta tags SEO
    document.title = "Livre plié Jésus – Création artisanale | Les Carnets de Lolotte";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Portrait de Jésus en livre plié, pièce unique faite main à partir d\'un livre ancien. Création poétique et spirituelle – Les Carnets de Lolotte.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'livre plié Jésus, art sacré, décoration spirituelle, création artisanale, pièce unique, art chrétien, Les Carnets de Lolotte');
    }

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      {/* Breadcrumb */}
      <section className="py-4 bg-[#FAF9F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center space-x-2 text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
            <a href="/" className="text-[#6B5E5A] hover:text-[#B97A72] transition-colors cursor-pointer">Accueil</a>
            <i className="ri-arrow-right-s-line text-[#B97A72]"></i>
            <a href="/boutique" className="text-[#6B5E5A] hover:text-[#B97A72] transition-colors cursor-pointer">Boutique</a>
            <i className="ri-arrow-right-s-line text-[#B97A72]"></i>
            <span className="text-[#6B5E5A]">Spirituel &amp; Symbolique</span>
            <i className="ri-arrow-right-s-line text-[#B97A72]"></i>
            <span className="text-[#B97A72] font-medium">{product.name}</span>
          </nav>
        </div>
      </section>

      {/* Product Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Image du produit */}
            <div>
              <div className="w-full h-[500px] bg-white rounded-lg overflow-hidden shadow-lg mb-4 cursor-pointer" onClick={() => setSelectedImage(productImage)}>
                <img
                  src={productImage}
                  alt="Jésus – Portrait en livre plié"
                  className="w-full h-full object-contain hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-lg p-4 text-center">
                <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <i className="ri-information-line text-[#B97A72] mr-2"></i>
                  Cliquez sur l'image pour l'agrandir
                </p>
              </div>
            </div>

            {/* Product Info */}
            <div className="space-y-8">
              <div>
                <div className="inline-block bg-[#C49B86] text-white px-4 py-2 rounded-full text-sm font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  ✨ Pièce unique
                </div>
                
                <h1 className="text-5xl font-bold text-[#B97A72] mb-6" style={{ fontFamily: 'Allura, cursive' }}>
                  {product.name}
                </h1>
                
                <p className="text-xl text-[#6B5E5A] leading-relaxed mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {product.description}
                </p>

                <div className="bg-[#FAF9F8] border-2 border-[#F8EDEA] rounded-xl p-6 mb-6">
                  <h3 className="text-xl font-semibold text-[#B97A72] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                    ✨ Une création pleine de sens
                  </h3>
                  <ul className="space-y-3 text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#B97A72] mr-3 mt-1"></i>
                      <span>Pièce unique faite main (pliage page par page)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#B97A72] mr-3 mt-1"></i>
                      <span>Livre ancien recyclé (pages imprimées visibles, charme vintage)</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#B97A72] mr-3 mt-1"></i>
                      <span>Idéal pour décoration spirituelle, coin prière, cadeau chrétien</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#B97A72] mr-3 mt-1"></i>
                      <span>Chaque création présente de légères variations liées au livre utilisé</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#B97A72] mr-3 mt-1"></i>
                      <span>Emballage soigné, prêt à offrir</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Specifications */}
              <div className="bg-white border-2 border-[#F8EDEA] rounded-xl p-6 space-y-4">
                <h3 className="text-2xl font-semibold text-[#B97A72] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Caractéristiques
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <span className="font-medium text-[#B97A72] w-32" style={{ fontFamily: 'Playfair Display, serif' }}>📏 Dimensions :</span>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>À renseigner (hauteur/largeur/profondeur)</span>
                  </div>
                  <div className="flex items-start">
                    <span className="font-medium text-[#B97A72] w-32" style={{ fontFamily: 'Playfair Display, serif' }}>📖 Matériaux :</span>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.details.materials}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="font-medium text-[#B97A72] w-32" style={{ fontFamily: 'Playfair Display, serif' }}>✨ Type :</span>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.details.type}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="font-medium text-[#B97A72] w-32" style={{ fontFamily: 'Playfair Display, serif' }}>🎨 Fabrication :</span>
                    <span className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.details.fabrication}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="font-medium text-[#B97A72] w-32" style={{ fontFamily: 'Playfair Display, serif' }}>🚚 Stock :</span>
                    <span className="text-[#C49B86] font-semibold" style={{ fontFamily: 'Poppins, sans-serif' }}>1 pièce disponible</span>
                  </div>
                </div>
              </div>

              {/* Price and Add to Cart */}
              <div className="bg-[#EBD2CF] border-2 border-[#B97A72] rounded-xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <span className="text-5xl font-bold text-[#B97A72]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {product.price}€
                    </span>
                    <p className="text-sm text-[#6B5E5A] mt-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      Livraison soignée incluse
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="w-16 h-16 flex items-center justify-center mx-auto mb-2">
                      <i className="ri-star-line text-4xl text-[#B97A72]"></i>
                    </div>
                    <p className="text-xs text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      Pièce unique
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <label className="text-[#6B5E5A] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Quantité :
                    </label>
                    <div className="flex items-center border-2 border-[#B97A72] rounded-full overflow-hidden">
                      <button 
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="px-4 py-2 bg-[#F8EDEA] text-[#B97A72] hover:bg-[#E8CBA8] transition-colors cursor-pointer"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        -
                      </button>
                      <span className="px-6 py-2 bg-white text-[#6B5E5A] font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {quantity}
                      </span>
                      <button 
                        onClick={() => setQuantity(Math.min(1, quantity + 1))}
                        className="px-4 py-2 bg-[#F8EDEA] text-[#B97A72] hover:bg-[#E8CBA8] transition-colors cursor-pointer"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={handleAddToCart}
                    className="w-full bg-[#B97A72] text-white py-4 rounded-full text-lg font-semibold hover:bg-[#C49B86] transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    <div className="w-6 h-6 flex items-center justify-center mr-3">
                      <i className="ri-shopping-cart-line text-xl"></i>
                    </div>
                    Ajouter au panier
                  </button>

                  <div className="flex items-center justify-center space-x-6 text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    <div className="flex items-center">
                      <i className="ri-truck-line mr-2 text-[#B97A72]"></i>
                      Livraison soignée
                    </div>
                    <div className="flex items-center">
                      <i className="ri-shield-check-line mr-2 text-[#B97A72]"></i>
                      Paiement sécurisé
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Description détaillée */}
      <section className="py-16 bg-[#FAF9F8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#B97A72] mb-6" style={{ fontFamily: 'Allura, cursive' }}>
              Une création spirituelle unique
            </h2>
            <div className="bg-white border-2 border-[#F8EDEA] rounded-xl p-8 shadow-lg">
              <p className="text-lg text-[#6B5E5A] leading-relaxed mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Ce portrait de Jésus en livre plié est une véritable œuvre d'art spirituelle. Chaque page a été pliée 
                avec soin et dévotion pour créer ce visage empreint de sérénité et de présence.
              </p>
              <p className="text-lg text-[#6B5E5A] leading-relaxed mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Réalisée à partir d'un livre ancien recyclé, cette création artisanale conserve le charme vintage 
                des pages imprimées tout en révélant une image pleine de symbolisme et d'émotion. Parfaite pour 
                décorer un coin prière, un espace de méditation ou comme cadeau chrétien significatif.
              </p>
              <p className="text-xl text-[#B97A72] font-medium italic mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                ✨ Une pièce unique qui apporte paix, présence et spiritualité à votre intérieur ✨
              </p>
              <div className="bg-[#F8EDEA] border-l-4 border-[#B97A72] p-6 rounded-lg">
                <p className="text-base text-[#6B5E5A] italic" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  💝 <strong>Idée cadeau :</strong> Cette création est parfaite pour offrir à un proche lors d'une 
                  communion, d'une confirmation, d'un baptême ou simplement pour témoigner votre foi et partager 
                  un moment de grâce.
                </p>
              </div>
            </div>
          </div>

          {/* Pourquoi choisir cette création */}
          <div className="bg-[#EBD2CF] border-2 border-[#B97A72] rounded-xl p-8 text-center">
            <h3 className="text-3xl font-bold text-[#B97A72] mb-6" style={{ fontFamily: 'Allura, cursive' }}>
              🕊️ Pourquoi choisir cette création ?
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-6">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-hand-heart-line text-3xl text-[#B97A72]"></i>
                </div>
                <h4 className="font-semibold text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Fait main avec amour
                </h4>
                <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Chaque pli est réalisé à la main avec patience et dévotion
                </p>
              </div>
              <div className="bg-white rounded-lg p-6">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-recycle-line text-3xl text-[#B97A72]"></i>
                </div>
                <h4 className="font-semibold text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Livre ancien recyclé
                </h4>
                <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Une seconde vie donnée aux livres, pour un charme authentique
                </p>
              </div>
              <div className="bg-white rounded-lg p-6">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-gift-line text-3xl text-[#B97A72]"></i>
                </div>
                <h4 className="font-semibold text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Prêt à offrir
                </h4>
                <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Emballage soigné pour faire plaisir dès la réception
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={closeModal}
              className="absolute -top-12 right-0 text-white text-2xl hover:text-gray-300 transition-colors cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
            <img
              src={selectedImage}
              alt="Image agrandie"
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Notification toast */}
      {notification.show && (
        <div 
          className={`fixed top-20 right-4 z-50 px-6 py-3 rounded-lg shadow-lg transition-all duration-300 ${
            notification.type === 'success' 
              ? 'bg-green-500 text-white' 
              : 'bg-red-500 text-white'
          }`}
          style={{ 
            transform: notification.show ? 'translateX(0)' : 'translateX(100%)',
            opacity: notification.show ? 1 : 0
          }}
        >
          <div className="flex items-center space-x-2">
            <i className={`${notification.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}`}></i>
            <span style={{ fontFamily: 'Poppins, sans-serif' }}>{notification.message}</span>
          </div>
        </div>
      )}
    </div>
  );
}
